import {Skeleton} from '@mui/material';
import Box from '@mui/material/Box';
import type * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import RenderIfVisible from 'react-render-if-visible';
import EmployeeCapacityTable from '@/components/Capacity/EmployeeCapacityTable';
import EmployeeCapacityTotalsTable from '@/components/Capacity/EmployeeCapacityTotalsTable';
import {usePreferencesState, useUpdatePreferences} from '@/context/PreferencesProvider';
import buildCapacityTotals from '@/functions/buildCapacityTotals';
import {useGetActuals} from '@/hooks/capacity/useGetActuals';
import {useGetCapacityCombined} from '@/hooks/capacity/useGetCapacityCombined';
import {useGetCapacityLabels} from '@/hooks/capacity/useGetCapacityLabels';
import type {CapacityConfig} from '@/types/CapacityConfig';
import type {CapacityDefault} from '@/types/CapacityDefault';
import type {CapacityOverride} from '@/types/CapacityOverride';
import type {ClientServiceTeam} from '@/types/ClientServiceTeam';
import type {Employee} from '@/types/Employee';

export type RawCapacity = Omit<CapacityDefault, 'capacityMonth'> & {
    capacityMonth : string;
};

export const mapRawCapacityDefault = (rawCapacity : RawCapacity) : CapacityDefault => {
    return {
        ...rawCapacity,
    };
};

export const mapRawCapacityOverride = (rawCapacity : RawCapacity) : CapacityOverride => {
    return {
        ...rawCapacity,
        capacityMonth: rawCapacity.capacityMonth,
    };
};

type Props = {
    employees : Employee[];
    teams : ClientServiceTeam[];
    fiscalMonth : string;
    defaultCapacityYearMonth : string;
    capacityConfig : CapacityConfig;
};

const CapacityList : React.FC<Props> = (
    {
        employees,
        teams,
        fiscalMonth,
        defaultCapacityYearMonth,
        capacityConfig,
    } : Props
) => {
    const {preferences} = usePreferencesState();
    const updatePreferences = useUpdatePreferences();
    const [capacityTotals, setCapacityTotals] = useState<Record<string, number> | null>(null);

    const {data: capacityLabels} = useGetCapacityLabels();
    const {data: capacityCombined} = useGetCapacityCombined({
        capacityYearMonth: fiscalMonth,
        employees: employees,
    });

    const {data: actuals} = useGetActuals({
        fiscalMonth,
        months: 8,
        capacityConfig,
        employees,
    });

    const sortedEmployees = useMemo(() => {
        return employees.sort((a : Employee, b : Employee) : number => {
            const aName = a.firstName + ' ' + a.lastName;
            const bName = b.firstName + ' ' + b.lastName;

            return aName.localeCompare(bName);
        });
    }, [employees]);

    const handleFavoriteChange = useCallback(async (employeeId : number, isFavorite : boolean) => {
        let newFavorites;

        if (isFavorite) {
            newFavorites = [...preferences.capacity.favoriteEmployees, employeeId];
        } else {
            newFavorites = preferences.capacity.favoriteEmployees.filter(favorite => favorite !== employeeId);
        }

        await updatePreferences({capacity: {favoriteEmployees: newFavorites}});
    }, [updatePreferences, preferences.capacity.favoriteEmployees]);

    useEffect(() => {
        if (capacityCombined && capacityLabels) {
            setCapacityTotals(buildCapacityTotals(
                sortedEmployees,
                capacityConfig,
                capacityCombined,
                fiscalMonth,
                capacityLabels
            ));
        }
    }, [capacityCombined, capacityLabels, sortedEmployees]);

    if (!capacityLabels || !capacityCombined || !capacityTotals) {
        return <Skeleton variant="rectangular" width="100%" height={300}/>;
    }

    return (<Box>
        <EmployeeCapacityTotalsTable
            fiscalMonth={fiscalMonth}
            defaultCapacityYearMonth={defaultCapacityYearMonth}
            capacityConfig={capacityConfig}
            capacityTotals={capacityTotals}
        />
        {sortedEmployees
            .map(employee => {
                return <RenderIfVisible defaultHeight={560} key={`capacity-${employee.id}`}>
                    <EmployeeCapacityTable
                        employee={employee}
                        teams={teams}
                        actual={actuals?.find(a => a.employeeId === employee.id)}
                        fiscalMonth={fiscalMonth}
                        capacityCombined={
                            capacityCombined.filter(cap => cap.associateID === employee.id)
                        }
                        labels={capacityLabels}
                        defaultCapacityYearMonth={defaultCapacityYearMonth}
                        capacityConfig={capacityConfig}
                        favorite={{
                            isFavorite: preferences.capacity.favoriteEmployees.includes(employee.id),
                            onFavoriteChange: handleFavoriteChange,
                        }}
                    />
                </RenderIfVisible>;
            })}
    </Box>);
};

export default CapacityList;
